import React from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Add from '@material-ui/icons/Add';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import UnfoldLess from '@material-ui/icons/UnfoldLess';
import UnfoldMore from '@material-ui/icons/UnfoldMore';
import axios from 'axios';
import { BASE_URL } from '../BASE_CONSTS';
import Cookies from '../provider/helpers/Cookies';
import {
  attachClientIds,
  createStatistic,
  duplicateStatistic,
  updateStatistic,
  removeStatistic,
  sanitizeStatisticsForStrapi,
  validateStatisticsForSave,
} from './statisticsModel';

const FIELD_GROUPS = [
  {
    title: 'Скорость и реакция',
    fields: [
      ['Hit', 'Сила удара', 'км/ч'],
      ['Speed', '10 метров с места', 'км/ч'],
      ['Speed2', '10 метров с места', 'секунды'],
      ['Speed_s_razbega', '10 метров с разбега', 'км/ч'],
      ['Speed_s_razbega2', '10 метров с разбега', 'секунды'],
      ['Reaction', 'Реакция', 'мс'],
    ],
  },
  {
    title: 'Прыжки и точность',
    fields: [
      ['Jump', 'Прыжок в высоту', 'см'],
      ['Jump2', 'Прыжок в длину', 'см'],
      ['sharpshooting', 'Точность', '%'],
      ['Agility', 'Agility test', 'секунды'],
    ],
  },
  {
    title: 'FootSkill и дриблинг',
    fields: [
      ['FootSkill', 'FootSkill test', 'секунды'],
      ['FootSkill2', 'FootSkill test', 'удары'],
      ['MessiTestDriblingTest', 'Дриблинг тест', 'секунды'],
      ['ExstraColorDriblingTest', 'Дриблинг тест', 'касания'],
      ['MessiTestDriblingTest2', 'Дриблинг тест 2', 'секунды'],
      ['ExstraColorDriblingTest2', 'Дриблинг тест 2', 'касания'],
    ],
  },
];

const today = () => new Date().toISOString().slice(0, 10);
const displayDate = (value) => {
  if (!value) return 'Без даты';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : String(value);
};

const uniqueDates = (statistics) => {
  const seen = new Set();
  const result = [];
  (statistics || []).forEach((item) => {
    const date = String(item.date || '');
    if (!seen.has(date)) {
      seen.add(date);
      result.push(date);
    }
  });
  return result;
};

const StatIterator = ({ record }) => {
  const playerId = record && record.id;
  const [statistics, setStatistics] = React.useState([]);
  const [footballPlayer, setFootballPlayer] = React.useState(null);
  const [loading, setLoading] = React.useState(Boolean(playerId));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [saved, setSaved] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState({});
  const [activeDate, setActiveDate] = React.useState('');
  const dateRefs = React.useRef({});

  const fetchData = React.useCallback(() => {
    if (!playerId) return;
    const token = Cookies.getCookie('token');
    setLoading(true);
    fetch(`${BASE_URL}/footballers/${playerId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const rows = attachClientIds(data.Statistics || []);
        setFootballPlayer(data);
        setStatistics(rows);
        setActiveDate(rows[0]?.date || '');
        setError('');
      })
      .catch((fetchError) => setError(`Не удалось загрузить статистику: ${fetchError.message}`))
      .finally(() => setLoading(false));
  }, [playerId]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  React.useEffect(() => {
    const dates = uniqueDates(statistics);
    if (!dates.length) {
      if (activeDate) setActiveDate('');
      return;
    }
    if (!dates.includes(activeDate)) setActiveDate(dates[0]);
  }, [statistics, activeDate]);

  if (!playerId) {
    return (
      <div className="fs-stat-editor fs-stat-editor--create-empty">
        <div className="fs-admin-empty">
          Сначала сохраните футболиста. После создания здесь можно будет добавлять любое количество результатов, в том числе несколько на одну дату.
        </div>
      </div>
    );
  }
  if (loading) return <div className="fs-admin-empty">Загрузка статистики…</div>;

  const dates = uniqueDates(statistics);
  const currentDateIndex = Math.max(0, dates.indexOf(activeDate));

  const scrollToDate = (date) => {
    setActiveDate(date);
    window.requestAnimationFrame(() => {
      const node = dateRefs.current[date || '__empty'];
      if (node && node.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const moveDate = (delta) => {
    if (!dates.length) return;
    const next = Math.min(dates.length - 1, Math.max(0, currentDateIndex + delta));
    scrollToDate(dates[next]);
  };

  const collapseAll = () => {
    const state = {};
    statistics.forEach((item) => { state[item.clientId] = true; });
    setCollapsed(state);
  };

  const expandAll = () => setCollapsed({});

  const change = (clientId, field) => (event) => {
    setSaved(false);
    setStatistics((rows) => updateStatistic(rows, clientId, field, event.target.value));
  };

  const addNewStat = () => {
    const row = createStatistic(activeDate || today());
    setStatistics((rows) => [...rows, row]);
    setCollapsed((state) => ({ ...state, [row.clientId]: false }));
    setActiveDate(row.date || '');
    setSaved(false);
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        const node = document.getElementById(`stat-${row.clientId}`);
        if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 20);
    });
  };

  const duplicate = (row) => {
    const copy = duplicateStatistic(row);
    setStatistics((rows) => [...rows, copy]);
    setCollapsed((state) => ({ ...state, [copy.clientId]: false }));
    setActiveDate(copy.date || '');
    setSaved(false);
  };

  const deleteItem = (clientId) => {
    setStatistics((rows) => removeStatistic(rows, clientId));
    setSaved(false);
  };

  const saveData = async () => {
    if (!footballPlayer) return;
    const validation = validateStatisticsForSave(statistics);
    if (!validation.valid) {
      setSaved(false);
      setError(validation.error);
      return;
    }

    const cleanStatistics = sanitizeStatisticsForStrapi(statistics);
    setSaving(true);
    setError('');
    setSaved(false);
    const token = Cookies.getCookie('token');
    const payload = {
      ...footballPlayer,
      Statistics: cleanStatistics,
    };
    try {
      const response = await axios.put(`${BASE_URL}/footballers/${playerId}`, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data || payload;
      const rows = attachClientIds(data.Statistics || payload.Statistics);
      setFootballPlayer(data);
      setStatistics(rows);
      setSaved(true);
    } catch (saveError) {
      const apiMessage = saveError.response?.data?.message;
      const detail = apiMessage === 'ValidationError'
        ? 'Strapi отклонил одно из значений. Пустые результаты не отправляются; проверьте дату и заполненные числовые поля.'
        : (apiMessage || saveError.message);
      setError(`Не удалось сохранить статистику: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  let renderedFirstDate = new Set();

  return (
    <div className="fs-stat-editor">
      <div className="fs-stat-editor__toolbar">
        <div>
          <span className="fs-page-kicker">РЕЗУЛЬТАТЫ ТЕСТИРОВАНИЯ</span>
          <h2>Статистика игрока</h2>
          <p>Одинаковая дата разрешена: каждая карточка остаётся отдельным результатом.</p>
        </div>
        <Button className="fs-button-primary" variant="contained" onClick={addNewStat} startIcon={<Add />}>
          Добавить результат
        </Button>
      </div>

      {statistics.length ? (
        <div className="fs-stat-navigator">
          <div className="fs-stat-navigator__main">
            <Button className="fs-stat-nav-arrow" onClick={() => moveDate(-1)} disabled={currentDateIndex <= 0} aria-label="Предыдущая дата">
              <KeyboardArrowLeft />
            </Button>
            <div className="fs-stat-date-strip">
              {dates.map((date) => {
                const count = statistics.filter((item) => String(item.date || '') === date).length;
                return (
                  <button
                    key={date || '__empty'}
                    type="button"
                    className={`fs-stat-date-chip ${activeDate === date ? 'is-active' : ''}`}
                    onClick={() => scrollToDate(date)}
                  >
                    <strong>{displayDate(date)}</strong>
                    <span>{count} {count === 1 ? 'результат' : 'рез.'}</span>
                  </button>
                );
              })}
            </div>
            <Button className="fs-stat-nav-arrow" onClick={() => moveDate(1)} disabled={currentDateIndex >= dates.length - 1} aria-label="Следующая дата">
              <KeyboardArrowRight />
            </Button>
          </div>
          <div className="fs-stat-navigator__actions">
            <Button onClick={collapseAll} startIcon={<UnfoldLess />}>Свернуть все</Button>
            <Button onClick={expandAll} startIcon={<UnfoldMore />}>Развернуть все</Button>
          </div>
        </div>
      ) : null}

      {error ? <div className="fs-admin-alert fs-admin-alert--error">{error}</div> : null}
      {saved ? <div className="fs-admin-alert fs-admin-alert--success">Статистика сохранена.</div> : null}

      <div className="fs-stat-editor__list">
        {statistics.length === 0 ? <div className="fs-admin-empty">Статистики пока нет.</div> : null}
        {statistics.map((item, index) => {
          const isCollapsed = Boolean(collapsed[item.clientId]);
          const dateKey = String(item.date || '');
          const firstForDate = !renderedFirstDate.has(dateKey);
          renderedFirstDate.add(dateKey);
          return (
            <section
              className={`fs-stat-record ${activeDate === dateKey ? 'is-active-date' : ''}`}
              key={item.clientId}
              id={`stat-${item.clientId}`}
              ref={(node) => {
                if (firstForDate && node) dateRefs.current[dateKey || '__empty'] = node;
              }}
            >
              <header className="fs-stat-record__header">
                <div className="fs-stat-record__heading">
                  <span className="fs-stat-record__number">#{index + 1}</span>
                  <TextField
                    label="Дата тестирования"
                    type="date"
                    value={item.date || ''}
                    onChange={change(item.clientId, 'date')}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                  />
                </div>
                <div className="fs-stat-record__actions">
                  <Button onClick={() => duplicate(item)} startIcon={<FileCopyOutlined />}>Дублировать</Button>
                  <Button className="fs-button-danger" onClick={() => deleteItem(item.clientId)} startIcon={<DeleteOutline />}>Удалить</Button>
                  <Button
                    className="fs-stat-record__collapse"
                    onClick={() => setCollapsed((state) => ({ ...state, [item.clientId]: !isCollapsed }))}
                    aria-label={isCollapsed ? 'Развернуть' : 'Свернуть'}
                  >
                    {isCollapsed ? <ExpandMore /> : <ExpandLess />}
                  </Button>
                </div>
              </header>

              {!isCollapsed ? (
                <div className="fs-stat-record__body">
                  {FIELD_GROUPS.map((group) => (
                    <div className="fs-stat-group" key={group.title}>
                      <h3>{group.title}</h3>
                      <div className="fs-stat-grid">
                        {group.fields.map(([field, label, unit]) => (
                          <TextField
                            key={field}
                            variant="outlined"
                            size="small"
                            type="number"
                            label={label}
                            value={item[field] ?? ''}
                            onChange={change(item.clientId, field)}
                            inputProps={{ step: 'any' }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>

      <div className="fs-stat-editor__footer">
        <Button className="fs-button-primary" variant="contained" disabled={saving} onClick={saveData}>
          {saving ? 'Сохранение…' : 'Сохранить статистику'}
        </Button>
      </div>
    </div>
  );
};

export default StatIterator;
